const isChesapeakeTheme = document.querySelector('body[data-theme-variant="chesapeake"]');

const SHOPIFY_CONFIG = {
  storefrontAccessToken: isChesapeakeTheme ? '48a8c8634314973cc3219af2dd69c47f' : '92319d6e9368255090ebb64b4af840ac',
  apiVersion: '2025-04',
};

const STOREFRONT_PRODUCT_QUERY = `
  query getProducts($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        id
        metafield(namespace: "magento", key: "badges") {
          value
          type
        }
        handle
        title
        totalInventory
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        compareAtPriceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        variants(first: 10) {
          edges {
            node {
              id
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              }
              quantityAvailable
              availableForSale
            }
          }
        }
      }
    }
  }
`;

let pricingMutationObserver;
let currentPricingHandlers = [];
let initCheckInterval;
let lastContainerClassState = '';
let lastDropdownLabelValue = '';
let hasInitializedPricing = false;

async function fetchProductData(productId) {
  const url = `/api/${SHOPIFY_CONFIG.apiVersion}/graphql.json`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.storefrontAccessToken,
      },
      body: JSON.stringify({
        query: STOREFRONT_PRODUCT_QUERY,
        variables: {
          ids: [productId],
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.errors) {
      console.error('GraphQL errors:', data.errors);
      return null;
    }

    return data.data.nodes[0] || null;
  } catch (error) {
    console.error('Error fetching product data:', error);
    return null;
  }
}

function getCurrentPrice(product) {
  if (!product || !product.variants || !product.variants.edges.length) {
    return null;
  }

  const allVariants = product.variants.edges.map((edge) => edge.node);

  if (allVariants.length === 0) {
    return null;
  }

  const prices = allVariants
    .map((variant) => {
      const regularPrice = parseFloat(variant.price.amount);
      return regularPrice;
    })
    .filter((price) => !isNaN(price) && price > 0);

  if (prices.length === 0) {
    return null;
  }

  return Math.min(...prices);
}

function formatPrice(price, currencyCode = 'USD') {
  if (price === null || price === undefined) {
    return 'N/A';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
  }).format(price);
}

function hasAnyProductOnSale(products) {
  return products.some((product) => {
    if (!product || !product.variants || !product.variants.edges.length) {
      return false;
    }

    return product.variants.edges.some((edge) => {
      const variant = edge.node;
      const price = parseFloat(variant.price.amount);
      const compareAtPrice = variant.compareAtPrice ? parseFloat(variant.compareAtPrice.amount) : null;

      return compareAtPrice && compareAtPrice > price;
    });
  });
}

function getProductBadge(product) {
  if (!product || !product.metafield) {
    return null;
  }
  const rawValue = product.metafield.value;
  if (typeof rawValue !== 'string') return null;
  return rawValue;
}

function anyChildHasNewBadge(products) {
  return products.some((product) => {
    const badge = getProductBadge(product);
    if (!badge) return false;
    return badge
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .includes('new');
  });
}

function areAllChildProductsOutOfStock(products) {
  if (!products || products.length === 0) {
    return true;
  }

  return products.every((product) => {
    if (!product || !product.variants || !product.variants.edges.length) {
      return true;
    }

    return product.variants.edges.every((edge) => {
      const variant = edge.node;
      const quantityAvailable = variant.quantityAvailable || 0;
      const availableForSale = variant.availableForSale;
      return quantityAvailable <= 0 && !availableForSale;
    });
  });
}

function updateShopNowButton(container, isOutOfStock) {
  const quickAddContainer = container.querySelector('.quick-add');
  if (!quickAddContainer) {
    return;
  }

  const submitButton = quickAddContainer.querySelector('.quick-add__submit');
  if (!submitButton) {
    return;
  }

  if (isOutOfStock) {
    submitButton.innerHTML = '<span>Out of Stock</span>';
    submitButton.disabled = true;
    submitButton.style.pointerEvents = 'none';
    submitButton.style.opacity = '0.6';
    submitButton.removeAttribute('onclick');
  } else {
    const productHandle = quickAddContainer.getAttribute('data-product-handle');
    submitButton.innerHTML = '<span>Shop Now</span>';
    submitButton.disabled = false;
    submitButton.style.pointerEvents = 'all';
    submitButton.style.opacity = '1';
    if (productHandle) {
      submitButton.setAttribute('onclick', `window.location.href='${productHandle}'`);
    }
  }
}

function updateSaleBadge(container, showSale) {
  const badgesContainer = container.querySelector('.c-badges');
  if (!badgesContainer) {
    return;
  }

  const existingSaleBadge = badgesContainer.querySelector('.c-badge--on-sale');

  if (showSale && !existingSaleBadge) {
    const saleBadge = document.createElement('span');
    saleBadge.className = 'c-badge c-badge--on-sale';
    saleBadge.textContent = 'On Sale';
    badgesContainer.appendChild(saleBadge);
  } else if (!showSale && existingSaleBadge) {
    existingSaleBadge.remove();
  }
}

function updateNewBadge(container, showNew) {
  const badgesContainer = container.querySelector('.c-badges');
  if (!badgesContainer) {
    return;
  }

  const existingNewBadge = badgesContainer.querySelector('.c-badge--new');

  if (showNew && !existingNewBadge) {
    const newBadge = document.createElement('span');
    newBadge.className = 'c-badge c-badge--new';
    newBadge.textContent = 'New';
    badgesContainer.appendChild(newBadge);
  } else if (!showNew && existingNewBadge) {
    existingNewBadge.remove();
  }
}

async function updateChildProductPrices() {
  const elementsWithChildren = document.querySelectorAll('[data-children-products]');

  for (const element of elementsWithChildren) {
    const childProductsValue = element.getAttribute('data-children-products');

    if (!childProductsValue || childProductsValue.trim() === '') {
      continue;
    }

    const productIds = childProductsValue
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id);

    if (productIds.length === 0) {
      continue;
    }

    try {
      const productDataPromises = productIds.map((id) => fetchProductData(id));
      const productDataResults = await Promise.all(productDataPromises);

      const validProducts = productDataResults.filter((product) => product !== null);

      if (validProducts.length === 0) {
        continue;
      }

      const currentPrices = validProducts.map((product) => getCurrentPrice(product)).filter((price) => price !== null);

      if (currentPrices.length === 0) {
        continue;
      }

      const lowestPrice = Math.min(...currentPrices);
      const currencyCode = validProducts[0]?.priceRange?.minVariantPrice?.currencyCode || 'USD';

      const priceContainer = element.closest('.c-custom-card-product') || element.parentElement;
      const priceElement = priceContainer?.querySelector('.c-custom-card-product__starting-price');

      if (priceElement) {
        priceElement.textContent = formatPrice(lowestPrice, currencyCode);
      }

      const isOnSale = hasAnyProductOnSale(validProducts);
      updateSaleBadge(priceContainer, isOnSale);

      const hasNew = anyChildHasNewBadge(validProducts);
      updateNewBadge(priceContainer, hasNew);

      const allOutOfStock = areAllChildProductsOutOfStock(validProducts);
      updateShopNowButton(priceContainer, allOutOfStock);
    } catch (error) {
      console.error('Error processing child products:', error);
    }
  }
}

function toggleStartingAtLabels() {
  const elementsWithChildren = document.querySelectorAll('[data-children-products]');

  elementsWithChildren.forEach((element) => {
    const childProductsValue = element.getAttribute('data-children-products');
    const hasChildren = childProductsValue && childProductsValue.trim() !== '';

    const priceContainer =
      element.closest('.c-custom-card-product, .product-card, [class*="product"]') || element.parentElement;
    const startingFromContainer = priceContainer?.querySelector('.c-custom-card-product__starting-from');

    if (startingFromContainer) {
      if (hasChildren) {
        startingFromContainer.style.display = 'block';
      } else {
        startingFromContainer.style.display = 'none';
      }
    }
  });
}

function cleanupChildProductPricingState() {
  currentPricingHandlers.forEach((handler) => {
    if (handler.element && handler.element.parentNode) {
      const newElement = handler.element.cloneNode(true);
      handler.element.parentNode.replaceChild(newElement, handler.element);
    }
  });

  currentPricingHandlers = [];

  const saleBadges = document.querySelectorAll('.c-badge--on-sale');
  saleBadges.forEach((badge) => {
    const productCard = badge.closest('.c-custom-card-product, .product-card, [class*="product"]');
    if (productCard && productCard.querySelector('[data-children-products]')) {
      badge.remove();
    }
  });
}

function getCurrentContainerClassState(container) {
  return container ? container.className : '';
}

function getCurrentDropdownLabelValue() {
  const dropdownLabel = document.querySelector('.kuSearchResultsPageContainer .kuDropdown .kuDropdownLabel');
  return dropdownLabel ? dropdownLabel.textContent.trim() : '';
}

function setupPricingMutationObserver() {
  let targetContainer = document.querySelector('.kuSearchResultsPageContainer');

  if (!targetContainer) {
    targetContainer = document.body;
  }

  lastContainerClassState = getCurrentContainerClassState(targetContainer);
  lastDropdownLabelValue = getCurrentDropdownLabelValue();

  if (pricingMutationObserver) {
    pricingMutationObserver.disconnect();
  }

  pricingMutationObserver = new MutationObserver(function (mutations) {
    let shouldReinitializePricing = false;
    let containerClassChanged = false;
    let dropdownLabelChanged = false;
    let newChildProductsFound = false;

    const searchResultsContainer = document.querySelector('.kuSearchResultsPageContainer');
    if (searchResultsContainer && targetContainer !== searchResultsContainer) {
      pricingMutationObserver.disconnect();
      targetContainer = searchResultsContainer;
      lastContainerClassState = getCurrentContainerClassState(targetContainer);
      setupPricingMutationObserver();
      return;
    }

    mutations.forEach(function (mutation) {
      if (mutation.type === 'attributes' && mutation.target === targetContainer && mutation.attributeName === 'class') {
        const currentClassState = getCurrentContainerClassState(targetContainer);
        if (currentClassState !== lastContainerClassState) {
          containerClassChanged = true;
          lastContainerClassState = currentClassState;
        }
      }

      if (mutation.type === 'childList' || mutation.type === 'characterData') {
        const currentDropdownValue = getCurrentDropdownLabelValue();
        if (currentDropdownValue !== lastDropdownLabelValue) {
          dropdownLabelChanged = true;
          lastDropdownLabelValue = currentDropdownValue;
        }
      }

      if (mutation.target.matches && mutation.target.matches('.kuDropdownLabel')) {
        const currentDropdownValue = getCurrentDropdownLabelValue();
        if (currentDropdownValue !== lastDropdownLabelValue) {
          dropdownLabelChanged = true;
          lastDropdownLabelValue = currentDropdownValue;
        }
      }

      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const hasChildProducts = node.querySelector && node.querySelector('[data-children-products]');
            const isProductContainer =
              node.matches &&
              (node.matches('.product-card, .c-custom-card-product, [class*="product"]') ||
                node.querySelector('.product-card, .c-custom-card-product, [class*="product"]'));

            if (hasChildProducts || isProductContainer) {
              shouldReinitializePricing = true;

              if (!hasInitializedPricing && hasChildProducts) {
                newChildProductsFound = true;
              }
            }
          }
        });
      }

      if (mutation.type === 'attributes' && mutation.target !== targetContainer) {
        const target = mutation.target;
        if (target.hasAttribute && target.hasAttribute('data-children-products')) {
          shouldReinitializePricing = true;
        }
      }
    });

    if (containerClassChanged || shouldReinitializePricing || dropdownLabelChanged || newChildProductsFound) {
      clearTimeout(window.pricingReinitTimeout);

      let delay = 500;
      if (containerClassChanged) delay = 1000;
      if (dropdownLabelChanged) delay = 800;
      if (newChildProductsFound) delay = 100;

      window.pricingReinitTimeout = setTimeout(function () {
        cleanupChildProductPricingState();
        initializeChildProductPricing();
      }, delay);
    }
  });

  pricingMutationObserver.observe(targetContainer, {
    childList: true,
    subtree: true,
    attributes: true,
    characterData: true,
    attributeFilter: ['data-children-products', 'class'],
  });

  const dropdownLabel = document.querySelector('.kuSearchResultsPageContainer .kuDropdown .kuDropdownLabel');
  if (dropdownLabel) {
    const dropdownObserver = new MutationObserver(function (mutations) {
      const currentDropdownValue = getCurrentDropdownLabelValue();
      if (currentDropdownValue !== lastDropdownLabelValue) {
        lastDropdownLabelValue = currentDropdownValue;

        clearTimeout(window.pricingReinitTimeout);
        window.pricingReinitTimeout = setTimeout(function () {
          cleanupChildProductPricingState();
          initializeChildProductPricing();
        }, 800);
      }
    });

    dropdownObserver.observe(dropdownLabel, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
    });
  }
}

async function initializeChildProductPricing() {
  hasInitializedPricing = true;
  toggleStartingAtLabels();
  await updateChildProductPrices();
}

function checkForChildProductElements() {
  const elementsWithChildren = document.querySelectorAll('[data-children-products]');

  if (elementsWithChildren.length > 0) {
    clearInterval(initCheckInterval);
    initializeChildProductPricing();
    return true;
  }

  return false;
}

function startInitializationCheck() {
  setupPricingMutationObserver();

  if (checkForChildProductElements()) {
    return;
  }

  let checkCount = 0;
  const maxChecks = 10;
  initCheckInterval = setInterval(() => {
    checkCount++;

    if (checkForChildProductElements()) {
      return;
    }

    if (checkCount >= maxChecks) {
      clearInterval(initCheckInterval);
    }
  }, 500);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startInitializationCheck);
} else {
  startInitializationCheck();
}
